import { jsx as _jsx } from "react/jsx-runtime";
import { createRoot } from 'react-dom/client';
import { isCustomElementRegistered } from './utils';
/**
 * Registrerer/oppretter en ny webkomponent.
 *
 * Eksempel:
 * registerWebComp(HeaderWrapper, { name: 'hn-webcomp-header', templateId: 'hn-webcomp-header-template' });
 * @param ChildComponent Reactkomponent som skal rendres i webkomponenten
 * @param config Konfigurasjon for webkomponenten
 */
function registerWebComponent(ChildComponent, config) {
    if (isCustomElementRegistered(config.name)) {
        return;
    }
    class WebComponent extends HTMLElement {
        _root;
        _observer;
        _props = {};
        constructor() {
            super();
            // Støtte for komplekse properties i React 19 (callbacks, objekter, arrays, etc.)
            config.properties?.forEach(propName => {
                Object.defineProperty(this, propName, {
                    configurable: true,
                    get: () => {
                        return this._props[propName];
                    },
                    set: (value) => {
                        this._props[propName] = value;
                        this.renderReactComponent();
                    },
                });
            });
        }
        connectedCallback() {
            let mountPoint;
            const mountPointId = config.id || config.name;
            // Håndter gjenbruk av shadowRoot (f.eks. ved flytting i DOM)
            if (this.shadowRoot) {
                mountPoint = this.shadowRoot.getElementById(mountPointId);
            }
            else {
                const shadowRoot = this.attachShadow({ mode: config.mode || 'open' });
                const tmplId = config.templateId || `${config.name}-template`;
                const template = document.getElementById(tmplId);
                if (config.templateId && !template && config.onError) {
                    const errorMsg = `Kunne ikke finne html <template id="${config.templateId}">. Sjekk at templateId er riktig.`;
                    config.onError(errorMsg);
                }
                if (template) {
                    shadowRoot.appendChild(template.content.cloneNode(true));
                }
                mountPoint = document.createElement(config.rootTagName || 'div');
                mountPoint.id = mountPointId;
                if (config.className) {
                    mountPoint.className = config.className;
                }
                shadowRoot.appendChild(mountPoint);
            }
            this._root = createRoot(mountPoint, {
                identifierPrefix: config.identifierPrefix || `${config.name}_`,
            });
            this.renderReactComponent();
            // Observer håndterer endringer i HTML-attributter (strenger)
            this._observer = new MutationObserver(() => this.renderReactComponent());
            this._observer.observe(this, { attributes: true });
        }
        disconnectedCallback() {
            this._root?.unmount();
            this._root = undefined;
            this._observer?.disconnect();
        }
        getProps() {
            // 1. Vanlige HTML-attributter (alltid strenger).
            const attrProps = {};
            for (const attr of this.attributes) {
                attrProps[attr.name] = attr.value;
            }
            // 2. Komplekse properties fra React 19.
            return { ...attrProps, ...this._props };
        }
        renderReactComponent() {
            this._root?.render(_jsx(ChildComponent, { ...this.getProps() }));
        }
    }
    window.customElements.define(config.name, WebComponent);
}
export default registerWebComponent;
